import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiMenu, FiX } from 'react-icons/fi';
import { FaMusic, FaLanguage, FaHeadphones, FaSignInAlt } from 'react-icons/fa';

const Navigation: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeLink, setActiveLink] = useState('');
  
  // Handle scroll effect for navbar
  useEffect(() => {
    const handleScroll = () => {
      const offset = window.scrollY;
      if (offset > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Handle link hover
  const handleLinkHover = (link: string) => {
    setActiveLink(link);
  };

  return (
    <motion.nav 
      className={`fixed top-0 left-0 right-0 z-50 px-6 py-4 transition-all duration-300 ${
        scrolled 
          ? 'bg-black/80 backdrop-blur-lg shadow-lg' 
          : 'bg-transparent'
      }`}
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
    >
      <div className="max-w-7xl mx-auto flex justify-between items-center">
        {/* Logo and Brand */}
        <Link to="/" className="flex items-center group">
          <div className="relative">
            <img 
              src="https://kcgdwjmwmfciiiryhagj.supabase.co/storage/v1/object/public/englishupload/logoamalieen.png" 
              alt="Amalie AI" 
              className="h-10 w-auto transition-all duration-300 group-hover:scale-110"
            />
            
            {/* Animated musical notes around logo */}
            <motion.div 
              className="absolute -top-1 -right-1 text-red-400 text-xs"
              animate={{ 
                rotate: [0, 10, 0],
                scale: [1, 1.2, 1],
                opacity: [0.7, 1, 0.7]
              }}
              transition={{ 
                duration: 2,
                repeat: Infinity,
                repeatType: "reverse"
              }}
            >
              ♪
            </motion.div>
            <motion.div 
              className="absolute -bottom-1 -left-1 text-red-400 text-xs"
              animate={{ 
                rotate: [0, -10, 0],
                scale: [1, 1.2, 1],
                opacity: [0.7, 1, 0.7]
              }}
              transition={{ 
                duration: 2,
                delay: 0.5,
                repeat: Infinity,
                repeatType: "reverse"
              }}
            >
              ♫
            </motion.div>
          </div>
          
          <div className="ml-3">
            <span className="text-xl font-bold bg-gradient-to-r from-red-400 to-red-200 bg-clip-text text-transparent">
              Amalie AI
            </span>
          </div>
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center space-x-1">
          <NavLink 
            href="#how-it-works" 
            label="How it Works" 
            icon={<FaHeadphones className="w-4 h-4" />}
            isActive={activeLink === 'how-it-works'}
            onHover={() => handleLinkHover('how-it-works')}
          />
          
          <NavLink 
            href="#features" 
            label="Features" 
            icon={<FaMusic className="w-4 h-4" />}
            isActive={activeLink === 'features'}
            onHover={() => handleLinkHover('features')}
          />
          
          <NavLink 
            href="/login" 
            label="Sign In" 
            icon={<FaSignInAlt className="w-4 h-4" />}
            isActive={activeLink === 'signin'}
            onHover={() => handleLinkHover('signin')}
            isLink
          />
          
          <Link 
            to="/register" 
            className="ml-2 bg-gradient-to-r from-red-400 to-red-500 text-white px-5 py-2.5 rounded-full hover:from-red-300 hover:to-red-400 transition-all duration-300 font-medium flex items-center transform hover:scale-105 shadow-[0_4px_15px_rgba(239,68,68,0.3)]"
            onMouseEnter={() => handleLinkHover('register')}
            onMouseLeave={() => handleLinkHover('')}
          >
            <span className="mr-2">Start Learning</span>
            <motion.div
              animate={{ 
                scale: [1, 1.2, 1],
              }}
              transition={{ 
                duration: 1.5,
                repeat: Infinity,
              }}
            >
              <FaLanguage className="w-5 h-5" />
            </motion.div>
          </Link>
        </div>

        {/* Mobile Menu Button */}
        <button 
          className="md:hidden text-white focus:outline-none z-50"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-label={isMenuOpen ? "Close menu" : "Open menu"}
        >
          <motion.div
            initial={false}
            animate={{ rotate: isMenuOpen ? 90 : 0 }}
            transition={{ duration: 0.3 }}
          >
            {isMenuOpen ? <FiX className="w-6 h-6" /> : <FiMenu className="w-6 h-6" />}
          </motion.div>
        </button>
      </div>

      {/* Mobile Menu */}
      <motion.div 
        className={`md:hidden fixed inset-0 bg-black/95 backdrop-blur-xl z-40 flex flex-col items-center justify-center ${isMenuOpen ? 'block' : 'hidden'}`}
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: isMenuOpen ? 1 : 0, y: isMenuOpen ? 0 : -20 }}
        transition={{ duration: 0.3 }}
      >
        <div className="flex flex-col items-center space-y-8 py-8">
          <MobileNavLink 
            href="#how-it-works" 
            label="How it Works" 
            icon={<FaHeadphones className="w-5 h-5" />}
            onClick={() => setIsMenuOpen(false)}
          />
          
          <MobileNavLink 
            href="#features" 
            label="Features" 
            icon={<FaMusic className="w-5 h-5" />}
            onClick={() => setIsMenuOpen(false)}
          />
          
          <MobileNavLink 
            href="/login" 
            label="Sign In" 
            icon={<FaSignInAlt className="w-5 h-5" />}
            onClick={() => setIsMenuOpen(false)}
            isLink
          />
          
          <Link 
            to="/register" 
            className="bg-gradient-to-r from-red-400 to-red-500 text-white px-8 py-3 rounded-full hover:from-red-300 hover:to-red-400 transition-all duration-300 font-medium flex items-center"
            onClick={() => setIsMenuOpen(false)}
          >
            <span className="mr-2">Start Learning</span>
            <FaLanguage className="w-5 h-5" />
          </Link>
        </div>
        
        {/* Animated Background Elements */}
        <div className="absolute inset-0 -z-10 overflow-hidden pointer-events-none">
          {[...Array(10)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute text-yellow-500/5 text-6xl"
              initial={{ 
                x: `${Math.random() * 100}%`, 
                y: `${Math.random() * 100}%`,
                rotate: Math.random() * 360
              }}
              animate={{ 
                y: [null, `${Math.random() * 100}%`],
                rotate: [null, Math.random() * 360 + 180]
              }}
              transition={{ 
                duration: Math.random() * 10 + 10, 
                repeat: Infinity,
                ease: "linear"
              }}
            >
              {['♪', '♫', '♬', '♩', '♭', '♮', '♯'][Math.floor(Math.random() * 7)]}
            </motion.div>
          ))}
        </div>
      </motion.div>
    </motion.nav>
  );
};

// Desktop Navigation Link Component
interface NavLinkProps {
  href: string;
  label: string;
  icon: React.ReactNode;
  isActive: boolean;
  onHover: () => void;
  isLink?: boolean;
}

const NavLink: React.FC<NavLinkProps> = ({ href, label, icon, isActive, onHover, isLink = false }) => {
  const LinkComponent = isLink ? Link : 'a';
  
  return (
    <LinkComponent
      to={isLink ? href : undefined}
      href={!isLink ? href : undefined}
      className="relative px-4 py-2 rounded-full group"
      onMouseEnter={onHover}
      onMouseLeave={() => onHover()}
    >
      <span className="relative z-10 flex items-center text-sm font-medium transition-colors duration-300 group-hover:text-red-400">
        <span className="mr-2">{icon}</span>
        {label}
      </span>
      
      {/* Hover Background Effect */}
      <span className={`absolute inset-0 rounded-full bg-white/5 backdrop-blur-sm opacity-0 transform scale-90 transition-all duration-300 ${
        isActive ? 'opacity-100 scale-100' : 'group-hover:opacity-100 group-hover:scale-100'
      }`}></span>
      
      {/* Underline Animation */}
      <span className={`absolute bottom-0 left-1/2 transform -translate-x-1/2 h-0.5 bg-red-400 transition-all duration-300 ${
        isActive ? 'w-1/2' : 'w-0 group-hover:w-1/2'
      }`}></span>
    </LinkComponent>
  );
};

// Mobile Navigation Link Component
interface MobileNavLinkProps {
  href: string;
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  isLink?: boolean;
}

const MobileNavLink: React.FC<MobileNavLinkProps> = ({ href, label, icon, onClick, isLink = false }) => {
  const LinkComponent = isLink ? Link : 'a';
  
  return (
    <LinkComponent
      to={isLink ? href : undefined}
      href={!isLink ? href : undefined}
      className="flex items-center text-xl font-medium text-white hover:text-red-400 transition-colors duration-300"
      onClick={onClick}
    >
      <motion.div
        whileHover={{ scale: 1.1, rotate: 5 }}
        className="mr-3"
      >
        {icon}
      </motion.div>
      {label}
    </LinkComponent>
  );
};

export default Navigation;